// Based on https://github.com/olup/react-hook-mousetrap/blob/master/index.js
import mousetrap from "mousetrap";
import "mousetrap-global-bind";
import { useEffect, useRef } from "react";

/**
 * Use mousetrap hook
 *
 * @param  {(string | string[])} handlerKey - A key, key combo or array of combos according to Mousetrap documentation.
 * @param  { function } handlerCallback - A function that is triggered on key combo catch.
 * @param  { string } evtType - A string that specifies the type of event to listen for. It can be 'keypress', 'keydown' or 'keyup'.
 */
export default function useMousetrap(
  handlerKey: string | string[],
  handlerCallback: (
    evt: mousetrap.ExtendedKeyboardEvent,
    combo: string
  ) => void,
  {
    evtType = "keypress",
    bindMode = "global"
  }: {
    evtType?: "keypress" | "keydown" | "keyup";
    bindMode?: "global" | "nonInputFocused";
  } = {}
): void {
  // Use a ref instead of passing this as a dependency to useEffect to avoid
  // re-binding the event listener on every render (or requiring callers to
  // wrap the callback in useCallback)
  const actionRef = useRef<typeof handlerCallback>(handlerCallback);
  actionRef.current = handlerCallback;

  useEffect(() => {
    if (bindMode === "nonInputFocused") {
      mousetrap.bind(
        handlerKey,
        (evt, combo) => {
          typeof actionRef.current === "function" &&
            actionRef.current(evt, combo);
        },
        evtType
      );
      return () => {
        mousetrap.unbind(handlerKey);
      };
    } else {
      // @ts-ignore: mousetrap-global-bind doesn't have a type definition
      mousetrap.bindGlobal(
        handlerKey,
        (evt: mousetrap.ExtendedKeyboardEvent, combo: string) => {
          typeof actionRef.current === "function" &&
            actionRef.current(evt, combo);
        },
        evtType
      );
      return () => {
        // @ts-ignore: mousetrap-global-bind doesn't have a type definition
        mousetrap.unbindGlobal(handlerKey);
      };
    }
  }, [handlerKey, evtType, bindMode]);
}
